// =========================================================
// Vojjin DOM jQuery-like Wrapper
// =========================================================
import {registerGlobal} from "./core.js";
/* global Motion */
/* global Slider */
/* global Logger */

class DomWrapper {

    /**
     * Creates an instance that encapsulates DOM element selection and manipulation.
     *
     * @param {String|Element|NodeList|Array} selector - A CSS selector string, DOM element, NodeList, or array of elements to initialize with.
     * @param {Document|Element} [root=document] - The root element or document to scope the selection to.
     * @return {void}
     */
    constructor(selector, root = document) {
        this.elements = [];
        this.element = null;
        if (typeof selector === "string") {
            this.elements = Array.from(root.querySelectorAll(selector));
        } else if (selector instanceof Element) {
            this.elements = [selector];
        } else if (selector instanceof NodeList || Array.isArray(selector)) {
            this.elements = Array.from(selector);
        } else {
            this.elements = [];
        }
        if (this.elements.length > 0) this.element = this.elements[0];
    }

    /**
     * Checks if the current context has defined elements.
     *
     * This method evaluates whether the `elements` property contains one or more items.
     *
     * @function
     * @returns {boolean} True if the `elements` array has at least one item; otherwise, false.
     */
    defined = () => {
        return this.elements.length > 0;
    }

    /**
     * Iterates over each element in the `elements` array and executes the provided callback function
     * with the current element and its index as arguments. The `this` context of the callback function
     * is bound to the current element being processed.
     *
     * @param {Function} callback - A function that is executed for each element in the `elements` array.
     *                              Receives the current element and its index as arguments.
     * @returns {Object} The current object to allow method chaining.
     */
    each = (callback) => {
        this.elements.forEach((el, i) => callback(el, el, i));
        return this;
    }

    eachWrapper = (callback) => {
        this.elements.forEach((el, i) => callback(new DomWrapper(el), el, i));
        return this;
    };
    /**
     * Handles the retrieval and assignment of CSS styles for elements.
     *
     * @function
     * @param {string|Object} prop - A CSS property name as a string, or an object containing multiple CSS property-value pairs.
     * @param {string} [value] - The value to assign to the specified CSS property. If omitted, the current computed style of the property is returned.
     * @returns {string|undefined|Object} - Returns the computed style value of the specified property if `value` is not provided.
     *                                      If `prop` is an object or `value` is provided, returns the instance of the object for chaining after applying styles.
     */
    css = (prop, value) => {
        if (typeof prop === "string" && value === undefined) return this.element ? getComputedStyle(this.element)[prop] : undefined;
        if (typeof prop === "string" && value !== undefined) return this.each(el => el.style[prop] = value);
        if (typeof prop === "object") return this.each(el => Object.assign(el.style, prop));
        return this;
    }

    /**
     * Gets or sets the inner HTML content of the selected element(s).
     *
     * If called without an argument, this function retrieves the inner HTML of the first matched element.
     * If called with a `value` argument, it sets the inner HTML content of all matched elements to the specified value.
     *
     * @param {string} [value] - The HTML string to set as the content of the matched element(s). If omitted, the current inner HTML is returned.
     * @returns {string|null|object} - Returns the inner HTML content of the first matched element if no `value` is provided. Returns `null` if no element is matched.
     * If a `value` is provided, returns the current context (e.g., for chaining).
     */
    html = (value) => {
        if (value === undefined) return this.element?.innerHTML ?? null;
        return this.each(el => el.innerHTML = value);
    }

    /**
     * Gets or sets the text content of the selected elements.
     *
     * If no value is provided, returns the text content of the first element
     * in the collection or null if no elements are present.
     * If a value is provided, sets the text content of all selected elements
     * to the specified value.
     *
     * @param {string} [value] - The text content to set for the selected elements.
     *                           If omitted, the method retrieves the current text content.
     * @returns {string|null|Object} - The current text content of the first element
     *                                 if value is not specified, null if no elements
     *                                 are present, or the current object for method chaining
     *                                 when setting a value.
     */
    text = (value) => {
        if (value === undefined) return this.element?.textContent ?? null;
        return this.each(el => el.textContent = value);
    }

    /**
     * Gets or sets the value property for the selected element(s).
     *
     * When no argument is provided, this method retrieves the value of the first selected
     * element, if it exists. If the element does not have a value or does not exist,
     * it returns `null`.
     *
     * When a value is provided as an argument, this method sets the value property
     * of each selected element to the specified value.
     *
     * @param {*} [value] - The value to set for the selected element(s). If omitted, the method retrieves the value of the first selected element.
     * @returns {*} The value of the first selected element if no argument is passed. For setting a value, the current context is returned to allow for method chaining.
     */
    val = (value) => {
        if (value === undefined) return this.element?.value ?? null;
        return this.each(el => el.value = value);
    }

    /**
     * Handles the retrieval and setting of attributes on DOM elements.
     *
     * @param {string} name - The name of the attribute to retrieve or set.
     * @param {*} [value] - The value to set for the specified attribute.
     *                                      If undefined, the method retrieves the current value of the attribute.
     * @returns {*} - Returns the attribute value when `value` is not provided.
     *                                    Returns `null` if the attribute does not exist.
     *                                    When setting the attribute, returns `undefined` to allow method chaining.
     */
    attr = (name, value) => {
        if (value === undefined) return this.element?.getAttribute(name) ?? null;
        return this.each(el => el.setAttribute(name, value));
    }

    /**
     * Gets or sets the value of a custom data attribute for the selected element(s).
     *
     * When called with a single `key` argument, it retrieves the corresponding data attribute
     * value from the `dataset` of the first matched element. If the attribute does not exist,
     * it returns `null`.
     *
     * When called with both `key` and `value` arguments, it sets the specified data attribute
     * on all matched elements to the provided `value`.
     *
     * If the `value` parameter is not provided, the function acts as a getter. Otherwise, it
     * acts as a setter.
     *
     * @param {string} key - The name of the data attribute (without the `data-` prefix).
     * @param {string} [value] - The value to set for the specified data attribute. If omitted,
     *                            the method retrieves the current value.
     * @returns {string|null|Object} When getting, returns the value of the data attribute or
     *                                `null` if not found. When setting, returns the context
     *                                (`this`) for method chaining.
     */
    data = (key, value) => {
        if (value === undefined) return this.element?.dataset?.[key] ?? null;
        return this.each(el => el.dataset[key] = value);
    }

    /**
     * Adds a specified class to each element within a collection.
     *
     * This function iterates over each element in the collection and adds the specified
     * class name to the element's class list. If the class name already exists on the
     * element, it will not be added again as `classList.add` ensures uniqueness.
     *
     * @param {string} name - The class name to be added to each element.
     * @returns {Object} The current instance for chaining.
     */
    addClass = (name) => {
        return this.each(el => el.classList.add(name));
    }

    /**
     * Removes a specified CSS class from each element in the collection.
     *
     * @param {string} name - The name of the CSS class to be removed.
     * @returns {Object} The instance of the current object for chaining.
     */
    removeClass = (name) => {
        return this.each(el => el.classList.remove(name));
    }

    /**
     * Toggles a specified CSS class on each element in a collection.
     *
     * @param {string} name - The name of the CSS class to toggle.
     * @returns {Object} The current context to allow method chaining.
     */
    toggleClass = (name) => {
        return this.each(el => el.classList.toggle(name));
    }

    hasClass = (name) => {
        return this.element?.classList.contains(name);
    }




    hide = (speed = 0) => {
        return this.each(el => {
            const currentDisplay = getComputedStyle(el).display;
            if (currentDisplay === "none") return;

            el.dataset.prevDisplay = currentDisplay;

            // Store original spacing only once
            if (!el.dataset.originalPaddingTop) {
                const cs = getComputedStyle(el);
                el.dataset.originalPaddingTop = cs.paddingTop;
                el.dataset.originalPaddingBottom = cs.paddingBottom;
                el.dataset.originalMarginTop = cs.marginTop;
                el.dataset.originalMarginBottom = cs.marginBottom;
                el.dataset.originalHeight = el.scrollHeight + "px";
            }

            if (speed <= 0) {
                el.style.display = "none";
                return;
            }

            // Prepare animation
            const height = el.scrollHeight + "px";
            el.style.transition = `opacity ${speed}ms ease, height ${speed}ms ease, margin ${speed}ms ease, padding ${speed}ms ease`;
            el.style.overflow = "hidden";
            el.style.height = height;
            el.style.opacity = "1";
            el.offsetHeight; // Force reflow

            // Animate to hidden
            el.style.opacity = "0";
            el.style.height = "0";
            el.style.paddingTop = "0";
            el.style.paddingBottom = "0";
            el.style.marginTop = "0";
            el.style.marginBottom = "0";

            setTimeout(() => {
                el.style.display = "none";
                el.style.removeProperty("height");
                el.style.removeProperty("overflow");
                el.style.removeProperty("transition");
            }, speed);
        });
    };


    show = (speed = 0) => {
        return this.each(el => {
            const prevDisplay = el.dataset.prevDisplay || "block";
            const isHidden = getComputedStyle(el).display === "none";
            if (!isHidden) return;

            // Retrieve stored padding and margin
            const pTop = el.dataset.originalPaddingTop || "0px";
            const pBottom = el.dataset.originalPaddingBottom || "0px";
            const mTop = el.dataset.originalMarginTop || "0px";
            const mBottom = el.dataset.originalMarginBottom || "0px";
            const originalHeight = el.dataset.originalHeight || "auto";

            if (speed <= 0) {
                el.style.display = prevDisplay;
                el.style.removeProperty("opacity");
                return;
            }

            el.style.display = prevDisplay;
            el.style.overflow = "hidden";
            el.style.opacity = "0";
            el.style.height = "0";
            el.style.paddingTop = "0";
            el.style.paddingBottom = "0";
            el.style.marginTop = "0";
            el.style.marginBottom = "0";

            const targetHeight = originalHeight; //el.scrollHeight + "px";
            el.offsetHeight; // Force reflow

            // Animate open
            el.style.transition = `opacity ${speed}ms ease, height ${speed}ms ease, margin ${speed}ms ease, padding ${speed}ms ease`;
            el.style.opacity = "1";
            el.style.height = targetHeight;
            el.style.paddingTop = pTop;
            el.style.paddingBottom = pBottom;
            el.style.marginTop = mTop;
            el.style.marginBottom = mBottom;

            setTimeout(() => {
                el.style.removeProperty("height");
                el.style.removeProperty("overflow");
                el.style.removeProperty("transition");
            }, speed);
        });
    };

    /**
     * Attaches an event listener to each element in a collection.
     *
     * @param {string} event - The name of the event to listen for (e.g., 'click', 'mouseover').
     * @param {Function} handler - The callback function to execute when the event is triggered.
     * @returns {Object} The current context to allow for method chaining.
     */
    on = (event, handler) => {
        return this.each(el => el.addEventListener(event, handler));
    }

    /**
     * Detaches an event listener from the specified event on each element
     * in the collection.
     *
     * @param {string} event - The name of the event to stop listening for.
     * @param {Function} handler - The function to be removed as the event listener.
     * @returns {Object} The current context for method chaining.
     */
    off = (event, handler) => {
        return this.each(el => el.removeEventListener(event, handler));
    }

    /**
     * Dispatches a custom event with the specified name and detail object on each element in the context.
     *
     * @param {string} event - The name of the custom event to be triggered.
     * @param {Object} [detail={}] - An optional object containing additional data to be passed with the event.
     * @returns {*} The context after the event is dispatched for chaining purposes.
     */
    trigger = (event, detail = {}) => {
        return this.each(el => el.dispatchEvent(new CustomEvent(event, {detail})));
    }

    /**
     * Appends the specified content to each element in the collection.
     *
     * @param {string|Element} content - The content to be appended. It can be a string
     * containing HTML markup or a single DOM Element.
     *
     * If the content is a string, it is inserted as HTML at the end of each element.
     * If the content is a DOM Element, its clone is appended to the end of each element.
     * The original element remains unchanged.
     *
     * @returns {Object} The current object for chainable method calls.
     */
    append = (content) => {
        return this.each(el => {
            if (typeof content === 'string') el.insertAdjacentHTML('beforeend', content); else if (content instanceof Element) el.appendChild(content.cloneNode(true));
        });
    }

    /**
     * Inserts specified content as the first child of each element in the collection.
     *
     * If the content parameter is a string, it is interpreted as HTML and inserted
     * as the first child. If the content is a DOM element, it is cloned and then
     * inserted as the first child of each element in the collection.
     *
     * @param {string|Element} content - The content to insert before the first child of each element in the collection.
     *                                  Can be a string of HTML or a DOM element.
     * @returns {Object} The current collection, for method chaining.
     */
    prepend = (content) => {
        return this.each(el => {
            if (typeof content === 'string') el.insertAdjacentHTML('afterbegin', content); else if (content instanceof Element) el.insertBefore(content.cloneNode(true), el.firstChild);
        });
    }

    /**
     * Removes each element in the collection from the DOM.
     *
     * This method iterates over each element in the collection
     * and removes it from its parent node, effectively deleting it from the document.
     *
     * @function
     * @returns {Object} The current instance to allow method chaining.
     */
    remove = () => {
        return this.each(el => el.remove());
    }

    /**
     * Retrieves the parent element of the current DOM element wrapped in a `DomWrapper` instance.
     * If the current element does not have a parent element, returns `null`.
     *
     * @returns {DomWrapper|null} A `DomWrapper` instance representing the parent element, or `null` if no parent exists.
     */
    parent = () => {
        return this.element?.parentElement ? new DomWrapper(this.element.parentElement) : null;
    }

    /**
     * Retrieves the next sibling element of the current DOM element wrapped by this class.
     * If the current element has a next sibling, it returns a new instance of DomWrapper wrapping that sibling.
     * If there is no next sibling, it returns null.
     *
     * @returns {DomWrapper|null} A new DomWrapper instance representing the next sibling element,
     * or null if no next sibling exists.
     */
    next = () => {
        return this.element?.nextElementSibling ? new DomWrapper(this.element.nextElementSibling) : null;
    }

    /**
     * Retrieves the previous sibling element of the current element wrapped in a `DomWrapper` instance,
     * if it exists. If there is no previous sibling, returns `null`.
     *
     * @function prev
     * @returns {DomWrapper|null} A `DomWrapper` instance wrapping the previous sibling element, or `null` if no previous sibling exists.
     */
    prev = () => {
        return this.element?.previousElementSibling ? new DomWrapper(this.element.previousElementSibling) : null;
    }

    /**
     * Finds the closest ancestor (including the element itself) that matches a given selector.
     *
     * @param {string} selector - A CSS selector to match against.
     * @returns {DomWrapper|null} A new DomWrapper instance wrapping the closest matching ancestor, or null if none found.
     */
    closest = (selector) => {
        if (!this.defined()) return null;
        const found = this.elements.map(el => el.closest(selector)).filter(Boolean);
        return found.length ? new DomWrapper(found) : null;
    };
    /**
     * Searches for elements that match a specified CSS selector within the current set of elements.
     *
     * @param {string} selector - A string containing a CSS selector to match elements against.
     * @returns {DomWrapper} A new DomWrapper instance containing the elements that match the selector.
     */
    find = (selector) => {
        const found = this.elements.flatMap(el => Array.from(el.querySelectorAll(selector)));
        return new DomWrapper(found);
    }

    /**
     * Wraps each selected element with the given HTML structure.
     *
     * The method creates a wrapper element using the provided HTML string
     * and places it around each of the selected elements. The original elements
     * are appended as child nodes of the newly created wrapper element.
     *
     * @param {string} html - A string containing the HTML structure to be used as the wrapper.
     *                        This should represent a single root element.
     *
     * @returns {Object} The context object to allow for method chaining.
     */
    wrap = (html) => this.each(el => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();
        const wrapEl = wrapper.firstElementChild;
        el.parentNode.insertBefore(wrapEl, el);
        wrapEl.appendChild(el);
    });

    /**
     * Removes the parent element of each selected element, moving the selected elements' children
     * up one level in the DOM hierarchy. Effectively "unwraps" the selected elements from their parent element.
     *
     * If an element does not have a parent node, the operation is skipped for that element.
     *
     * This function modifies the DOM structure by:
     * - Moving all child nodes of the selected element into its parent before the element itself.
     * - Removing the selected element from the DOM.
     */
    unwrap = () => this.each(el => {
        const parent = el.parentNode;
        if (!parent) return;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
    });

    /**
     * Inserts specified content directly before each element in the set of matched elements.
     *
     * @param {string|Element} content - The content to insert. It can be a string containing HTML or
     *                                    an Element object. If a string is provided, the HTML is parsed
     *                                    and inserted before the target element(s). If an Element is
     *                                    provided, it is cloned and inserted before the target element(s).
     */
    before = (content) => this.each(el => {
        if (typeof content === 'string') el.insertAdjacentHTML('beforebegin', content); else if (content instanceof Element) el.parentNode.insertBefore(content.cloneNode(true), el);
    });

    /**
     * Inserts the specified content immediately after each element in the collection.
     *
     * @param {string|Element} content - The content to insert after each element.
     *                                    If a string is provided, it will be inserted as HTML.
     *                                    If an Element is provided, it will be cloned and inserted.
     * @returns {Object} The current object to allow method chaining.
     */
    after = (content) => this.each(el => {
        if (typeof content === 'string') el.insertAdjacentHTML('afterend', content); else if (content instanceof Element) el.parentNode.insertBefore(content.cloneNode(true), el.nextSibling);
    });

    /**
     * A function that clears the inner HTML content of each element in the context.
     * Iteratively processes each element and sets its innerHTML property to an empty string.
     */
    empty = () => this.each(el => el.innerHTML = '');

    /**
     * Creates a copy of the elements wrapped by the current instance.
     *
     * @param {boolean} [deep=true] - Determines whether the cloning process should include all child nodes of each element.
     *                                 If true, a deep clone is performed, replicating the entire DOM subtree of each element.
     *                                 If false, only the shallow structure of each element is cloned.
     * @returns {DomWrapper} A new instance of DomWrapper containing the cloned elements.
     */
    clone = (deep = true) => {
        const clones = this.elements.map(el => el.cloneNode(deep));
        return new DomWrapper(clones);
    }

    /**
     * Retrieves the children of the current set of DOM elements and wraps them in a new DomWrapper instance.
     *
     * The function iterates through each element in the current set of elements,
     * extracts their children, and combines them into a flattened array. This array
     * is then used to create and return a new DomWrapper object.
     *
     * @returns {DomWrapper} A new DomWrapper instance containing the children of the current elements.
     */
    children = () => new DomWrapper(this.elements.flatMap(el => Array.from(el.children)));

    /**
     * Retrieves all parent elements for each element in the current collection.
     *
     * This method traverses up the DOM tree from each element in the collection,
     * collecting all ancestor elements. The result is a flattened collection
     * of parent elements wrapped in a `DomWrapper` instance.
     *
     * @returns {DomWrapper} A new `DomWrapper` instance containing all parent elements
     *                       for the elements in the original collection.
     */
    parents = () => new DomWrapper(this.elements.flatMap(el => {
        const parents = [];
        let parent = el.parentElement;
        while (parent) {
            parents.push(parent);
            parent = parent.parentElement;
        }
        return parents;
    }));

    /**
     * Replaces each element in the current set of matched elements with new content.
     *
     * @param {string|Element} content - The content to replace the matched elements with.
     *                                  - If a string is provided, it is used as the new outer HTML of the element.
     *                                  - If an Element is provided, it replaces the element with a cloned version of that Element.
     */
    replace = (content) => this.each(el => {
        if (typeof content === 'string') el.outerHTML = content; else if (content instanceof Element) el.replaceWith(content.cloneNode(true));
    });

    /**
     * Calculates and returns the index of the current element within its parent's children.
     * If the element or its parent node does not exist, returns -1.
     *
     * @function
     * @returns {number} The zero-based index of the element within its parent's node list of children,
     *                   or -1 if the element or its parent is not present.
     */
    index = () => {
        if (!this.element || !this.element.parentNode) return -1;
        return [...this.element.parentNode.children].indexOf(this.element);
    }

    /**
     * Retrieves the type of the current element.
     * If the `element` property exists and contains a `type` property,
     * the method returns the value of the `type` property.
     * Otherwise, it returns `null`.
     *
     * @returns {string|null} The type of the element if it exists, otherwise `null`.
     */
    type = () => {
        return this.element?.type ? this.element.type : null;
    }

    offset = () => {
        if (!this.defined() || !this.element) return { top: 0, left: 0 };
        const el = this.element;
        const rect = el.getBoundingClientRect();
        return {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX
        };
    };

    width = () => {
        if (!this.defined() || !this.element) return 0;
        const el = this.element;
        return el.offsetWidth;
    };


    /**
     * Determines whether the associated element is checked.
     *
     * This function checks the `checked` property of the associated DOM element.
     * If the element exists and has a `checked` property, it returns its value.
     * Otherwise, it returns `false`.
     *
     * @returns {boolean} True if the element is checked, false otherwise.
     */
    checked = () => {
        return this.element?.checked ? this.element.checked : false;
    }

    // dom creation

    /**
     * Create a new element
     * @param {string} tag - element tag name
     * @param {object} [attrs] - attributes (id, class, data, style, etc.)
     * @param {string|Element|Array|DomWrapper} [content] - optional content
     * @returns {DomWrapper}
     */
    static create = (tag, attrs = {}, content = null) => {
        const el = document.createElement(tag);

        // assign attributes
        for (const [key, val] of Object.entries(attrs)) {
            if (key === 'class' || key === 'className') el.className = val;
            else if (key === 'style' && typeof val === 'object')
                Object.assign(el.style, val);
            else if (key.startsWith('data-'))
                el.setAttribute(key, val);
            else if (key in el) el[key] = val;
            else el.setAttribute(key, val);
        }

        // assign content
        if (content) {
            if (typeof content === 'string') el.innerHTML = content;
            else if (content instanceof Element) el.appendChild(content);
            else if (content instanceof this.constructor)
                content.each(c => el.appendChild(c.clone(true).element));
            else if (Array.isArray(content))
                content.forEach(c => {
                    if (typeof c === 'string') el.insertAdjacentHTML('beforeend', c);
                    else if (c instanceof Element) el.appendChild(c);
                });
        }

        return new DomWrapper(el);
    }

    /**
     * Create elements from raw HTML string
     * @param {string} html
     * @returns {DomWrapper}
     */
    static fromHTML = (html) => {
        const container = document.createElement('div');
        container.innerHTML = html.trim();
        return new DomWrapper(Array.from(container.childNodes).filter(n => n.nodeType === 1));
    }

    /**
     * Append current element(s) to another target
     * @param {string|Element|DomWrapper} target
     * @returns {DomWrapper}
     */
    appendTo = (target) => {
        const dest = target instanceof this.constructor ? target : new DomWrapper(target);
        this.each(el => dest.element?.appendChild(el));
        return this;
    }

    /**
     * Prepend current element(s) to another target
     * @param {string|Element|DomWrapper} target
     * @returns {DomWrapper}
     */
    prependTo = (target) => {
        const dest = target instanceof this.constructor ? target : new DomWrapper(target);
        this.each(el => dest.element?.insertBefore(el, dest.element.firstChild));
        return this;
    }

    /**
     * Replace the element with another content
     * @param {string|Element|DomWrapper} content
     */
    replaceWith = (content) => {
        this.each(el => {
            if (typeof content === 'string') el.outerHTML = content;
            else if (content instanceof Element) el.replaceWith(content.cloneNode(true));
            else if (content instanceof this.constructor && content.defined())
                el.replaceWith(content.element.cloneNode(true));
        });
        return this;
    }


    /**
     * Scrolls to the specific element with optional behavior and offset adjustments.
     *
     * @function
     * @param {Object} [opts={}] - The options for the scroll behavior.
     * @param {string} [opts.behavior='smooth'] - Specifies if the scrolling should be instant or smooth.
     * @param {number} [opts.offset=0] - Adjusts the scroll position by adding or subtracting the specified offset in pixels.
     * @throws Will return early if the required element is not defined.
     */
    scrollTo = (opts = {}) => {
        if (!this.defined()) return;
        const behavior = opts.behavior ?? 'smooth';
        const offset = opts.offset ?? 0;
        const rect = this.element.getBoundingClientRect();
        const scrollTop = window.scrollY + rect.top - offset;
        window.scrollTo({top: scrollTop, behavior});
    }

    /**
     * Scrolls an element into view within a specified container.
     *
     * This function calculates the position of an element relative to its container
     * and scrolls the container to bring the element into view, considering an optional offset.
     *
     * @param {string|DomWrapper} container - The container element in which the scrolling is to take place. Can be an HTMLElement or a query selector string.
     * @param {Object} [opts={}] - Options for customizing the scrolling behavior.
     * @param {number} [opts.offset=0] - The offset in pixels to adjust the scroll position.
     * @param {string} [opts.behavior='smooth'] - The scrolling behavior, e.g., 'smooth' or 'auto'.
     */
    scrollIn = (container, opts = {}) => {
        if (!this.defined()) return;
        container = new DomWrapper(container);
        if (!container.defined()) return;
        const rect = this.element.getBoundingClientRect();
        const cRect = container.element.getBoundingClientRect();
        const offset = opts.offset ?? 0;
        container.element.scrollTo({
            top: container.element.scrollTop + (rect.top - cRect.top - offset), behavior: opts.behavior ?? 'smooth'
        });
    }

    /**
     * Determines whether the element is visible within the viewport based on the specified threshold.
     *
     * The method calculates the position of the element relative to the viewport and checks if it intersects with
     * the visible area, considering the given threshold value. The element is considered "invisible" if it is undefined.
     *
     * @param {number} [threshold=0] - The distance in pixels from the edges of the viewport to adjust the visibility bounds.
     * @returns {boolean} - Returns true if the element is visible within the viewport, otherwise false.
     */
    isVisible = (threshold = 0) => {
        if (!this.defined()) return false;
        const rect = this.element.getBoundingClientRect();
        return (rect.top < window.innerHeight - threshold && rect.bottom > threshold && rect.left < window.innerWidth && rect.right > 0);
    }

    /**
     * Slides the current element using the specified options.
     * This method checks if the wrapper element is defined before performing the slide operation.
     * Logs an error if the wrapper element is not valid.
     *
     * @param {Object} [options={}] - An optional configuration object for customizing the slide behavior.
     * @returns {null|undefined} Returns null if the wrapper element is invalid, otherwise undefined.
     */
    slide = (options = {}) => {
        if (!this.defined()) {
            Logger.error('AutoSlider: invalid wrapper element');
            return null;
        }
        Slider.slide(this.element, options);
    }


    /**
     * Animates the transition of CSS properties from a starting state to a target state over a specified duration.
     *
     * The function infers the starting values of properties if only the target state is provided. It allows a variety of options
     * to control the animation such as custom easing, onUpdate callbacks, and onComplete actions.
     *
     * @param {...any} args - Variable arguments:
     *    1. (from, to, duration, opts): Specifies both the starting and target states explicitly.
     *    2. (to, duration, opts): Automatically determines the starting state from the computed styles of the element.
     *
     * The arguments handled within the function are described as:
     *
     * - `from` {Object}: An object defining the starting CSS property values. Each key represents a CSS property name.
     * - `to` {Object}: An object defining the target CSS property values. Each key represents a CSS property name.
     * - `duration` {number}: The duration of the animation in milliseconds. Defaults to 1000ms.
     * - `opts` {Object}: Additional options for the animation:
     *      - `easing` {Function}: Specifies the easing function to control the animation's progression. Defaults to `Easing.easeInOutQuad`.
     *      - `onUpdate` {Function}: A callback function triggered on each animation frame with the updated property values.
     *      - `onComplete` {Function}: A callback function triggered when the animation completes.
     *
     * @returns {Motion} Returns an instance of the `Motion` object that represents the animation. The `Motion` object allows further control of the animation such as pausing, resuming, or stopping.
     */
    motion = (...args) => {
        let from = {};
        let to = {};
        let duration = 1000;
        let opts = {};

        // Detect form: (from, to, duration, opts)
        if (typeof args[0] === 'object' && typeof args[1] === 'object') {
            from = args[0];
            to = args[1];
            duration = args[2] ?? 1000;
            opts = args[3] ?? {};
        }
        // Detect form: (to, duration, opts)
        else if (typeof args[0] === 'object') {
            to = args[0];
            duration = typeof args[1] === 'number' ? args[1] : 1000;
            opts = typeof args[2] === 'object' ? args[2] : {};
            // Auto-generate from current styles
            const computed = getComputedStyle(this.element);
            for (const key in to) {
                const cssKey = key.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
                const val = parseFloat(computed[cssKey] || computed[key]);
                from[key] = isNaN(val) ? 0 : val;
            }
        }

        const target = this.element;
        const unitProps = new Set([
            'width', 'height', 'top', 'left', 'right', 'bottom',
            'margin', 'marginTop', 'marginLeft', 'marginRight', 'marginBottom',
            'padding', 'paddingTop', 'paddingLeft', 'paddingRight', 'paddingBottom',
            'fontSize', 'lineHeight', 'letterSpacing', 'borderWidth', 'borderRadius'
        ]);

        const motion = new Motion(from, to, duration, {
            easing: opts.easing || "easeInOutQuad",
            onUpdate: (values) => {
                for (const key in values) {
                    const v = values[key];
                    if (key === 'opacity') target.style[key] = v;
                    else if (unitProps.has(key)) target.style[key] = v + 'px';
                    else target.style[key] = v;
                }
                opts.onUpdate?.(values);
            },
            onComplete: opts.onComplete || (() => {
            })
        });

        motion.start();
        return motion;
    }

    typeText = (opts = {}) => {
        const {
            duration = 2000,
            easing = "easeInOutQuad",
            attr = "data-text",
            delay = 0,
            loop = false
        } = opts;

        // Animate one element
        const animateElement = (el) => new Promise(resolve => {
            const fullText = el.getAttribute(attr) || el.textContent || "";
            el.textContent = "";
            this.attr(attr, fullText);
            const chars = [...fullText];
            const total = chars.length;
            const startTime = performance.now() + delay;

            const step = (now) => {
                const elapsed = Math.max(0, now - startTime);
                const t = Math.min(1, elapsed / duration);
                const eased = Easing.get(easing)(t);
                const visibleCount = Math.floor(eased * total);

                el.textContent = chars.slice(0, visibleCount).join("");

                if (t < 1) {
                    requestAnimationFrame(step);
                } else {
                    el.textContent = fullText;
                    if (loop) {
                        setTimeout(() => {
                            el.textContent = "";
                            requestAnimationFrame(() => animateElement(el));
                        }, 300);
                    } else {
                        resolve();
                    }
                }
            };

            requestAnimationFrame(step);
        });

        // Animate all elements in this DomWrapper
        const animations = this.elements.map(el => animateElement(el));
        return Promise.all(animations);
    };

}

const $$ = (selector, root = document) => new DomWrapper(selector, root);

// Attach static helpers to the factory
$$.create = DomWrapper.create;
$$.fromHTML = DomWrapper.fromHTML;
$$.DomWrapper = DomWrapper;

// Register globally
registerGlobal("$$", $$);

export {DomWrapper, $$};